import {first} from 'lodash';
import {Injectable} from '@angular/core';
import {AllPaymentMethodsAPIM, PaymentHistoryPaymentMethod, PaymentMethodType} from '@ox/typings';
import {BehaviorSubject, take, tap, Observable, of, map, switchMap, catchError, throwError} from 'rxjs';
import {ShopDataService} from '../shop-data/shop-data.service';
import {CopyFormattingService} from '../copy-formatting';

/** Service for formatting payment methods */
@Injectable()
export class PaymentMethodService {
  constructor(
    private shopDataService: ShopDataService,
    private copyService: CopyFormattingService
  ) {}

  // track current selected payment method
  private selectedPaymentSubject = new BehaviorSubject({} as AllPaymentMethodsAPIM);
  selectedPaymentMethod$ = this.selectedPaymentSubject.asObservable();

  get selectedPaymentMethod() {
    return this.selectedPaymentSubject.getValue();
  }

  set selectedPaymentMethod(val: AllPaymentMethodsAPIM) {
    this.selectedPaymentSubject.next(val);
  }

  // track all payment methods
  private paymentMethodsSubject = new BehaviorSubject<AllPaymentMethodsAPIM[]>([]);
  paymentMethods$ = this.paymentMethodsSubject.asObservable();

  get paymentMethods() {
    return this.paymentMethodsSubject.getValue();
  }

  set paymentMethods(val: AllPaymentMethodsAPIM[]) {
    this.paymentMethodsSubject.next(val);
  }

  // get payment methods for a given account
  private refreshTriggerSubject = new BehaviorSubject<boolean>(false);
  refreshTrigger$ = this.refreshTriggerSubject.asObservable();

  set refreshTrigger({updating, accountId}: { updating: boolean; accountId: string | null }) {
    this.refreshTriggerSubject.next(updating);
    if (updating && accountId) {
      this.refreshPaymentMethods(accountId).subscribe();
    }
  }

  refreshPaymentMethods = (accountId: string) => this.shopDataService.getPaymentMethods(accountId)
    .pipe(
        take(1),
        tap(({data: {paymentMethods}}) => {
          this.paymentMethods = paymentMethods;
          this.refreshTriggerSubject.next(false);
        }),
        catchError(err => throwError(() => new Error('Error refreshing payment methods')))
    );

  // This method uses the oc-payent endpoint - Unsure if this service is being used in other MFEs such as eMSP
  ocRefreshPaymentMethods = (accountId: string) => this.shopDataService.getOcPaymentMethods(accountId)
    .pipe(
        take(1),
        tap(({data: {paymentMethods}}) => {
          this.paymentMethods = paymentMethods;
          this.refreshTriggerSubject.next(false);
        }),
        catchError(err => throwError(() => new Error('Error refreshing payment methods')))
    );

  isPaymentMethodExpiring(paymentMethod: AllPaymentMethodsAPIM): boolean {
    const creditCardType = paymentMethod?.creditCard;
    if (creditCardType){
      const currentDate = new Date();
      const expirationDate = new Date(parseInt(creditCardType.expirationYear), parseInt(creditCardType.expirationMonth) - 1);
  
      // Calculate the difference in milliseconds
      const diff = expirationDate.getTime() - currentDate.getTime();
  
      // Convert the difference from milliseconds to days
      const days = diff / (1000 * 60 * 60 * 24);
      // If the difference is less than or equal to 90 days, return true
      if (days <= 90) {
        return true;
      }
    }
    return false;
  }

  isPaymentMethodExpired(paymentMethod: AllPaymentMethodsAPIM) {
    return paymentMethod?.creditCard?.expiryStatus === 'EXPIRED';
  }

  getPaymentMethodImageUrl(paymentMethod: AllPaymentMethodsAPIM): string {
    const {creditCard, ach, paypal} = paymentMethod;
    if (creditCard) {
      let iconName = '';
      if (/mastercard/i.test(creditCard.type)) iconName = 'master-card';
      else if (/american express/i.test(creditCard.type)) iconName = 'amex';
      else iconName = creditCard.type.toLowerCase();
      return 'content/dam/gmownercenter/global/resources/icons/symbol-defs.svg#icon-pay-' + iconName;
    } else if (ach) {
      return '/content/dam/gmownercenter/global/resources/icons/symbol-defs.svg#icon-bank';
    } else if (paypal) {
      return '/content/dam/gmownercenter/global/resources/icons/symbol-defs.svg#icon-pay-paypal';
    }

    return '';
  }

  getPaymentMethodShortName(paymentMethod: AllPaymentMethodsAPIM | PaymentHistoryPaymentMethod) {
    if (paymentMethod.creditCard)
      return `${paymentMethod.creditCard.type} ****${paymentMethod.creditCard.last4Digits}`;
    else if (paymentMethod.ach) 
      return `${paymentMethod.ach.accountType} ****${paymentMethod.ach.accountNumber}`;
    else if (paymentMethod.paypal) 
      return `PayPal - ${paymentMethod.paypal.payerCustomID}`;
    else return 'Payment Method'; 
  }

  getPaymentMethodDisplayName(paymentMethod: AllPaymentMethodsAPIM) {
    if (paymentMethod.creditCard)
      return `${paymentMethod.creditCard.type} <span>&#8226;&#8226;&#8226;&#8226;</span>${paymentMethod.creditCard.last4Digits}`;
    else if (paymentMethod.ach) 
      return `${paymentMethod.ach.accountType} <span>&#8226;&#8226;&#8226;&#8226;</span>${paymentMethod.ach.accountNumber}`;
    else if (paymentMethod.paypal) return 'PayPal';
    else return 'Payment Method';  
  }

  getPaymentMethodType(paymentMethod: AllPaymentMethodsAPIM): PaymentMethodType {
    if (paymentMethod.creditCard) return 'MTHD_CC';
    else if (paymentMethod.ach) return 'MTHD_ACH';
    else if (paymentMethod.paypal) return 'MTHD_PAYPAL';
    else return 'MTHD_CC';
  }

  isPaymentMethodAllowed = (paymentMethod: AllPaymentMethodsAPIM, allowedPaymentMethods: PaymentMethodType[]) => 
    allowedPaymentMethods?.includes(this.getPaymentMethodType(paymentMethod));

  /** This function sets the default selected card
   * 
   * The default card is selected in order of:
   * 1. The card is listed as a temporary method and is eligable for the current purchase
   * 2. The card is listed as default and is eligible for the current purchase
   * 2. The first card that is eligible for the current purchase
   * 3. The default card is selected even if it is not eligible for the current purchase (error will be unable to purchase)
   */
  setDefaultPaymentMethod = (allowedPaymentMethods: PaymentMethodType[]) => {
    return this.paymentMethods$.pipe(
      map((paymentMethods) => {
        const isTemporaryMethod = paymentMethods?.find((method) => method.isTemp);
        const defaultMethod = paymentMethods?.find((method) => method.isDefault);
        const firstAllowedMethod = paymentMethods?.find((method) => this.isPaymentMethodAllowed(method, allowedPaymentMethods));
    
        // First check if there is a temporary method on file 
        if (isTemporaryMethod && this.isPaymentMethodAllowed(isTemporaryMethod, allowedPaymentMethods))
          this.selectedPaymentMethod = isTemporaryMethod;
        // Second try the default method if it is allowed
        else if (defaultMethod && this.isPaymentMethodAllowed(defaultMethod, allowedPaymentMethods))
          this.selectedPaymentMethod = defaultMethod;
        // Third try any allowed method
        else if (firstAllowedMethod)
          this.selectedPaymentMethod = firstAllowedMethod;
        // Last set the default and allow it to be grayed out
        else if (defaultMethod) 
          this.selectedPaymentMethod = defaultMethod;
        else {
          const firstPaymentMethod = first(paymentMethods);
          if (firstPaymentMethod) this.selectedPaymentMethod = firstPaymentMethod;
        }
      }
    ));
  };

  /** Grabs the correct message for the user if they have payment methods that are not allowed for the current product */
  disabledPaymentMessage$(allowedPaymentMethods: PaymentMethodType[]): Observable<string | undefined> {
    return this.paymentMethods$.pipe(switchMap((paymentMethods) => {
      const unsupportedTypes: PaymentMethodType[] = [];
      paymentMethods.forEach((paymentMethod) => {
        if (!this.isPaymentMethodAllowed(paymentMethod, allowedPaymentMethods)) {
          unsupportedTypes.push(this.getPaymentMethodType(paymentMethod));
        }
      });

      // The user has both PAYPAL and ACH methods that are not allowed
      if (unsupportedTypes.includes('MTHD_PAYPAL') && unsupportedTypes.includes('MTHD_ACH')) {
        return this.copyService.getGenericCopy$('payment.disabled.paypal.ach');
      // The user has PAYPAL methods that are not allowed
      } else if (unsupportedTypes.includes('MTHD_PAYPAL')) {
        return this.copyService.getGenericCopy$('payment.disabled.paypal');
      // The user has ACH methods that are not allowed
      } else if (unsupportedTypes.includes('MTHD_ACH')) {
        return this.copyService.getGenericCopy$('payment.disabled.ach');
      // The user has unknown methods that are not allowed
      } else {
        return of(undefined);
      }
    }));
  }
}
